/* src/pages/admin/AdminCommentManager.js (Updated with Tabs) */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const AdminCommentManager = () => {
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('videos'); // 'videos', 'articles', or 'bookChapters'

    const fetchComments = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        let endpoint;
        if (activeTab === 'bookChapters') {
            endpoint = '/api/admin/books/admin/comments/book-chapters';
        } else {
            endpoint = `/api/admin/comments/${activeTab}`;
        }
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setComments(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            setError(`Failed to load ${activeTab} comments.`);
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [activeTab]); // The fetch logic re-runs whenever the activeTab changes

    useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    const handleDelete = async (commentId) => {
        if (window.confirm(`Are you sure you want to permanently delete this ${activeTab === 'bookChapters' ? 'book chapter' : activeTab.slice(0, -1)} comment?`)) {
            let endpoint;
            if (activeTab === 'bookChapters') {
                endpoint = `/api/admin/books/admin/comments/book-chapters/${commentId}`;
            } else {
                endpoint = `/api/admin/comments/${activeTab}/${commentId}`;
            }
            try {
                const token = localStorage.getItem('app_token');
                await axios.delete(endpoint, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Refresh the list after deletion
                fetchComments();
            } catch (err) {
                alert(`Error deleting comment. Please try again.`);
            }
        }
    };

    const renderTable = () => {
        if (isLoading) return <p>Loading comments...</p>;
        if (error) return <p className="error-message">{error}</p>;
        if (comments.length === 0) return <p>No comments found in this category.</p>;

        return (
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Comment</th>
                            <th>Author</th>
                            <th>In Response To</th>
                            <th>Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {comments.map(comment => (
                            <tr key={`${activeTab}-${comment.id}`}>
                                <td><p className="comment-cell">{comment.comment_text}</p></td>
                                <td>{comment.author_name || comment.name}</td>
                                <td>
                                    {activeTab === 'bookChapters' ? (
                                        <span>
                                            <b>Book:</b> {comment.book_title} <br/>
                                            <b>Chapter:</b> {comment.chapter_title}
                                        </span>
                                    ) : (
                                        <Link 
                                            to={`/${activeTab}/${comment.video_id || comment.article_id}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            {comment.content_title}
                                        </Link>
                                    )}
                                </td>
                                <td>{new Date(comment.created_at).toLocaleDateString()}</td>
                                <td className="admin-table-actions">
                                    <button onClick={() => handleDelete(comment.id)} className="action-btn delete" title="Delete Comment">
                                        <span className="material-icons">delete_forever</span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="admin-content-manager">
            <h3 className="admin-section-title">Comment Moderation</h3>

            {/* Tab Navigation */}
            <div className="course-tabs" style={{ marginBottom: '1.5rem' }}>
                <button
                    className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('videos')}
                >
                    Video Comments
                </button>
                <button
                    className={`tab-button ${activeTab === 'articles' ? 'active' : ''}`}
                    onClick={() => setActiveTab('articles')}
                >
                    Article Comments
                </button>
                <button
                    className={`tab-button ${activeTab === 'bookChapters' ? 'active' : ''}`}
                    onClick={() => setActiveTab('bookChapters')}
                >
                    Book Chapter Comments
                </button>
            </div>
            
            {renderTable()}
        </div>
    );
};

export default AdminCommentManager;
